import { useAbility } from "@casl/react";
import {
  AppliedFilters,
  Heading,
  Table,
  useFilters,
} from "@fluidattacks/design";
import type { IOptionsProps } from "@fluidattacks/design";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import capitalize from "lodash/capitalize";
import { useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import type { IGroupAttr } from "../types";
import { authzPermissionsContext } from "context/authz/config";
import { statusFormatter } from "features/vulnerabilities/formatters/status";
import type { TierType } from "gql/graphql";
import { useTable } from "hooks/use-table";
import { useFiltersStorage } from "storage/filters";

interface IOrganizationGroupsProps {
  readonly groups: IGroupAttr[];
}

export const OrganizationGroups: FC<IOrganizationGroupsProps> = ({
  groups,
}): JSX.Element => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const [filterDataset, setFilterDataset] = useState<IGroupAttr[]>([]);
  const [search, setSearch] = useState("");

  // Auxiliary functions
  const accessibleGroupsData = useCallback(
    (groupData: IGroupAttr[]): IGroupAttr[] =>
      groupData.filter((group): boolean =>
        group.permissions.includes(
          "integrates_api_mutations_update_group_managed_mutate",
        ),
      ),
    [],
  );

  const formatGroupsData = useCallback(
    (groupData: IGroupAttr[]): IGroupAttr[] =>
      groupData.map((group: IGroupAttr): IGroupAttr => {
        const name = capitalize(group.name);
        const tier = capitalize(group.tier);

        return { ...group, id: name, name, tier: tier as TierType };
      }),
    [],
  );

  const permissions = useAbility(authzPermissionsContext);
  const canSeeSubscriptionType = permissions.can(
    "see_billing_subscription_type",
  );

  const columns: ColumnDef<IGroupAttr>[] = [
    {
      accessorKey: "name",
      header: t("organization.tabs.billing.groups.headers.groupName"),
    },
    {
      accessorKey: "managed",
      cell: (cell): JSX.Element => cell.getValue() as JSX.Element,
      enableColumnFilter: false,
      header: t("organization.tabs.billing.groups.headers.managed"),
    },
    ...(canSeeSubscriptionType
      ? [
          {
            accessorKey: "tier",
            cell: (cell: CellContext<IGroupAttr, unknown>): JSX.Element =>
              statusFormatter(String(cell.getValue())),
            header: t("organization.tabs.billing.groups.headers.tier"),
          },
        ]
      : []),
    {
      accessorFn: (row): number | undefined => {
        return row.billing.numberAuthors;
      },
      accessorKey: "totalAuthors",
      cell: (cell): JSX.Element => statusFormatter(String(cell.getValue())),
      enableColumnFilter: false,
      header: t("organization.tabs.billing.groups.headers.numberAuthors"),
    },
    {
      accessorFn: (row): number | undefined => {
        return row.billing.costsTotal;
      },
      accessorKey: "costTotal",
      cell: (cell): JSX.Element => statusFormatter(String(cell.getValue())),
      enableColumnFilter: false,
      header: t("organization.tabs.billing.groups.headers.costsTotal"),
    },
  ];

  const optionsProps = useMemo(
    (): IOptionsProps<IGroupAttr>[] => [
      {
        filterOptions: [
          {
            key: "name",
            label: t("organization.tabs.billing.groups.headers.groupName"),
            type: "text",
          },
        ],
        label: t("organization.tabs.billing.groups.headers.groupName"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("billing_groups") as string | null;
  }, [getValues]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("billing_groups", value);
    },
    [setValues],
  );

  const dataset = useMemo(
    (): IGroupAttr[] => formatGroupsData(accessibleGroupsData(groups)),
    [groups, formatGroupsData, accessibleGroupsData],
  );

  const { Filters, options, removeFilter } = useFilters({
    dataset,
    getValues: handleGetValues,
    options: optionsProps,
    setFilteredDataset: setFilterDataset,
    setValues: handleSetValues,
  });

  const filteredDataset = useMemo(
    (): IGroupAttr[] =>
      filterDataset.filter((group): boolean =>
        group.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [filterDataset, search],
  );

  const { table } = useTable({
    columns,
    data: filteredDataset,
    id: "billing-groups-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      columnOptions: {
        hidden: ["managed", "tier", "costTotal"],
        order: ["name", "totalAuthors"],
        pinned: ["name", "totalAuthors"],
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
    },
    total: filteredDataset.length,
  });

  return (
    <div>
      <Heading fontWeight={"bold"} mb={2} mt={2} size={"md"}>
        {t("organization.tabs.billing.groups.title")}
      </Heading>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Table.Columns table={table} />
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
    </div>
  );
};
